import React, { createContext, useState } from "react";

//** Axios request */
import { request } from "../utilities/axios-utils.js";

//** Request/Response mapping */
import bindPolicyPaymentRequestMapping from "../mapping/bindPolicyPaymentRequestMapping.js";

//* Context
export const PaymentContext = createContext();

export const PaymentProvider = (props) => {
	const [tokenObject, setTokenObject] = useState({});
	const [transactionData, setTransactionData] = useState({});
	const [transactionNonceData, setTransactionNonceData] = useState(null);
	const [requestId, setRequestId] = useState(null);

	const [paymentMethod, setPaymentMethod] = useState(null); // Credit Card CC or Bank Details BA
	const [additionalInformation, setAdditionalInformation] = useState({
		nameOnCard: "",
		cardType: "",
	});

	const [secureCoCapturedData, setSecureCoCapturedData] = useState({});
	const [paymentFrequency, setPaymentFrequency] = useState("M"); // Monthly M or Annual A
	const [isPaymentMade, setIsPaymentMade] = useState(true);
	const [isFormValid, setIsFormValid] = useState(false);
	const [paymentGatewayError, setPaymentGatewayError] = useState(null);
	const [isGatewayError, setIsGatewayError] = useState(false);
	const [isContactCentre, setIsContactCentre] = useState(false);

	// const [policyNumber, setPolicyNumber] = useState("");
	//-----------------------------------------------------------------

	const getToken = async (accessToken) => {
		const response = await request({
			url: `/paymentToken`,
			method: "GET",
			accessToken: accessToken,
		});
		const tokenObject = response.data;
		setTokenObject(tokenObject);
	};

	const createCreditCardObject = () => {
		const ccObj = {
			cardHolderName: isContactCentre
				? secureCoCapturedData.cardHolderName
				: additionalInformation.nameOnCard,
			paymentMethod: paymentMethod,
			transactionNonceData: transactionNonceData,
			paymentFrequency: paymentFrequency,
			...(requestId && {
				requestId: requestId,
			}),
		};

		return ccObj;
	};

	const createBankAccObject = (requestData) => {
		const baObj = {
			paymentMethod: paymentMethod,
			bankBSB: requestData?.bankBSB.replace("-", ""),
			bankAccountNumber: requestData?.bankAccountNumber.replace(/\s/g, ""),
			bankAccountName: requestData?.bankAccountName,
			directDebitPermission: requestData?.bankPaymentPermissionGranted,
			paymentFrequency: paymentFrequency,
		};

		return baObj;
	};

	const paymentTransaction = async ({ accessToken, requestData }) => {
		const rawData =
			paymentMethod === "CC"
				? createCreditCardObject()
				: createBankAccObject(requestData);

		//* Map the data
		const mappedRequest = await bindPolicyPaymentRequestMapping(rawData);

		const bindPolicyResponse = await request({
			url: "/bindPolicy",
			method: "PUT",
			data: JSON.stringify(mappedRequest),
			accessToken: accessToken,
		});

		if (bindPolicyResponse.status !== 200) {
			//! Failure to bind policy
			setIsPaymentMade(true);
			setPaymentGatewayError(
				"Unfortunately we've experienced an error while processing your payment. Contact your bank for assistance, or use an alternative card or bank account to make your payment."
			);
			setIsGatewayError(true);
			setTransactionData({});
			return bindPolicyResponse.response.status;
		}

		setTransactionData(bindPolicyResponse.data);
		setIsPaymentMade(true);
		setIsGatewayError(false);
		setPaymentGatewayError(null);

		return bindPolicyResponse.status;
	};

	//* Ensure that the form is valid
	//* so we can set the state of the Purchase button
	const setFormValid = (formValid) => {
		setIsFormValid(formValid);
	};

	//* Pass the nonce data to the bind API.
	const setTransactionNonce = (nonceData) => {
		setTransactionNonceData(nonceData);
	};

	//* Set the name on card and card type - Required for API
	const setAdditionalInformationData = (additionalData) => {
		setAdditionalInformation(additionalData);
	};

	//* Set the Payment Frequency picked by the user. Monthly/Annual
	const setPaymentFrequencyData = (paymentFrequencyData) => {
		setPaymentFrequency(paymentFrequencyData);
	};

	//* Set the payment method, Credit Card or Bank.
	const setPaymentMethodData = (paymentMethodData) => {
		setPaymentMethod(paymentMethodData);
	};

	const setCapturedData = (capturedData) => {
		setSecureCoCapturedData(capturedData);
	};

	const setIsContactCentreData = (isContactCenter = false) => {
		setIsContactCentre(isContactCenter);
	};

	const setRequestIdData = (paymentRequestId) => {
		setRequestId(paymentRequestId);
	};

	const handleSetCaptureState = (capturedValue = false) => {
		setSecureCoCapturedData({
			...secureCoCapturedData,
			captured: capturedValue,
		});
	};

	return (
		<PaymentContext.Provider
			value={{
				tokenObject,
				paymentTransaction,
				isPaymentMade,
				setIsPaymentMade,
				transactionData,
				setTransactionData,
				setTransactionNonce,
				setFormValid,
				setAdditionalInformationData,
				setPaymentFrequencyData,
				setPaymentMethodData,
				setCapturedData,
				setIsContactCentreData,
				setRequestIdData,
				secureCoCapturedData,
				handleSetCaptureState,
				paymentMethod,
				isFormValid,
				getToken,
				paymentGatewayError,
				isGatewayError,
			}}
		>
			{props.children}
		</PaymentContext.Provider>
	);
};
